import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "/en/heritage/livsberattelser";
const SV_PATH = "/heritage/livsberattelser";

const USE_CASES = [
  "Interview with a parent or grandparent",
  "Family history",
  "Personal portrait",
  "Memorial film",
  "Filmed message to the family",
  "Documentation for the future",
];

const PROCESS = [
  { label: "First conversation", body: "We understand the purpose, who will be interviewed and how the film will be used within the family." },
  { label: "Planning and question outline", body: "We design the areas and questions together. The person can see the questions in advance if they wish." },
  { label: "Filmed interview", body: "Recording takes place in a calm, safe environment — at home or in a place with personal meaning." },
  { label: "Editing and post-production", body: "The material is edited with sound, colour and structure work. Photos and archive material can be woven in." },
  { label: "Delivery", body: "The film is delivered digitally directly to the family, ready to be shared privately." },
];

const PACKAGES = [
  { name: "The Life Interview", body: "A smaller format with one filmed interview with one person. Pre-meeting, question outline, filmed interview, editing and digital delivery." },
  { name: "The Family Legacy", body: "A more extensive format with a longer interview, chapter structure and the option to include photos or archive material. Pre-interview with the family, developed question outline, main film and shorter excerpts." },
  { name: "Legacy", body: "For families who want to create a more comprehensive filmed legacy with multiple interviews, clear narrative structure, archive material, main film, shorter clips and private delivery." },
];

const FAQ = [
  { q: "What is a filmed life story?", a: "A filmed life story is a structured interview where a person talks about their life, memories, family, work and experiences. The result is one or more films the family can preserve and share." },
  { q: "Who usually commissions a life story?", a: "Most often children or grandchildren who want to preserve a parent's or grandparent's story. Sometimes the person themselves takes the initiative." },
  { q: "Where is the interview recorded?", a: "In a setting that feels safe for the person being interviewed — at home, with a relative or in a place of personal meaning. We bring all the equipment." },
  { q: "How long is the film?", a: "A main film is typically 20–45 minutes, sometimes split into chapters. We can also produce shorter excerpts that are easier to watch day to day." },
  { q: "Can we use old photos or films?", a: "Yes. In The Family Legacy and Legacy we can weave in photographs, documents or older film material that complements the story." },
  { q: "Does the material stay private?", a: "Yes. The film is delivered directly to the family and is not shared further. You decide who gets to see it." },
];

export const Route = createFileRoute("/en_/heritage/livsberattelser")({
  head: () => ({
    meta: [
      { title: "Life Stories on film — Fenny Heritage" },
      { name: "description", content: "Filmed life stories for families. We help you document a loved one's life, memories and story in professional form." },
      { property: "og:title", content: "Life Stories on film — Fenny Heritage" },
      { property: "og:description", content: "Preserve a loved one's story through a professionally filmed interview. Safe process, high quality, private delivery." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/heritage/livsberattelser", name: "Life Stories on film — Fenny Heritage", description: "Filmed life stories for families. We help you document a loved one's life, memories and story in professional form."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Life Stories on film",
            path: PATH,
            description:
              "Fenny Heritage produces filmed life stories for families — structured interviews that document a person's life, memories and story.",
            serviceType: "Filmed life story",
            priceRange: "Quote after first conversation",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Home", path: "/en" },
            { name: "Heritage", path: "/en/heritage" },
            { name: "Life Stories", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: LifeStoriesPage,
});

function LifeStoriesPage() {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Heritage Personal</div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Filmed Life Stories
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            A filmed interview where a person tells the story of their life,
            upbringing, family, work, experiences and important memories —
            preserved for children, grandchildren and generations to come.
          </p>
          <QuickAnswer
            className="mt-6"
            text="A filmed life story is a structured interview where a person tells about their life, memories and experiences. The film is preserved digitally for the family and can be shared privately."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/en/contact" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90">
              Book a first conversation <ArrowRight className="size-4" />
            </Link>
            <Link to="/en/heritage" className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]">
              Back to Heritage
            </Link>
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Examples of use</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {USE_CASES.map((u) => (
            <li key={u} className="rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">{u}</li>
          ))}
        </ul>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">How it works</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10" id="packages">
        <h2 className="text-3xl font-semibold md:text-4xl">Packages</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PACKAGES.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
              <div className="font-medium">{p.name}</div>
              <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground/65">Pricing is provided as a quote after the first conversation.</p>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={PATH} />
      <SignatureCta />
    </article>
  );
}
